import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Layers, Pencil, X } from "lucide-react";

const TEMPLATE_OPTIONS = ["U6", "U8", "U10", "U12", "U14", "U16", "JUVENIL", "SENIOR", "PORTERO"];
const AGE_LABELS = {
  U6: "Escoleta (U6-U7)", U8: "Prebenjamín (U8-U9)", U10: "Benjamín (U10-U11)",
  U12: "Alevín (U12-U13)", U14: "Infantil (U14-U15)", U16: "Cadete (U16-U17)",
  JUVENIL: "Juvenil (U18-U19)", SENIOR: "Senior (Absoluto/Amateur)",
  PORTERO: "Portero (específica)",
};

export default function ClubCategories() {
  const [cats, setCats] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", template_key: "U12", order: 1 });

  const load = async () => {
    try { const { data } = await api.get("/categories"); setCats(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: "", template_key: "U12", order: cats.length + 1 }); setShowForm(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, template_key: c.template_key, order: c.order }); setShowForm(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/categories/${editing.id}`, form); toast.success("Categoría actualizada"); }
      else { await api.post("/categories", form); toast.success("Categoría creada"); }
      setShowForm(false); setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta categoría?")) return;
    try { await api.delete(`/categories/${id}`); toast.success("Eliminada"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="overline">Estructura del club</div>
          <h1 className="font-heading font-extrabold text-5xl tracking-tighter mt-2">Categorías</h1>
          <p className="text-muted-foreground text-sm mt-2">Añade, edita y ordena las categorías del club. Cada una lleva su plantilla de estadísticas por edad.</p>
        </div>
        <button data-testid="new-category-btn" onClick={openCreate} className="btn-volt"><Plus size={16} /> Nueva categoría</button>
      </div>

      <div className="border border-border">
        <table className="w-full text-sm">
          <thead className="border-b border-border">
            <tr className="text-left overline">
              <th className="p-3 w-12">#</th>
              <th className="p-3">Categoría</th>
              <th className="p-3">Plantilla</th>
              <th className="p-3 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {cats.map((c) => (
              <tr key={c.id} data-testid={`cat-row-${c.id}`} className="border-b border-border/60 hover:bg-white/[0.02]">
                <td className="p-3 font-mono text-muted-foreground">{c.order}</td>
                <td className="p-3 font-heading font-bold uppercase"><div className="flex items-center gap-2"><Layers size={14} className="text-primary" /> {c.name}</div></td>
                <td className="p-3">
                  <span className="px-2 py-1 border border-border font-mono text-xs">{c.template_key}</span>
                  <span className="text-muted-foreground text-xs ml-2">{AGE_LABELS[c.template_key] || ""}</span>
                </td>
                <td className="p-3 text-right">
                  <div className="flex justify-end gap-2">
                    <button data-testid={`edit-cat-${c.id}`} onClick={() => openEdit(c)} className="text-muted-foreground hover:text-primary"><Pencil size={15} /></button>
                    <button data-testid={`del-cat-${c.id}`} onClick={() => remove(c.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {cats.length === 0 && <tr><td colSpan={4} className="text-center p-8 text-muted-foreground text-sm">Sin categorías.</td></tr>}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={submit} className="bg-card border border-border w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="overline">{editing ? "Editar" : "Nueva"}</div>
                <div className="font-heading font-black text-2xl uppercase tracking-tight">{editing ? "Actualizar categoría" : "Añadir categoría"}</div>
              </div>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="p-1 hover:text-primary"><X size={18} /></button>
            </div>
            <div>
              <label className="overline block mb-1">Nombre *</label>
              <input required data-testid="cat-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Alevín A" className="w-full h-10 bg-transparent border border-border px-3 focus:border-primary focus:outline-none font-mono text-sm" />
            </div>
            <div>
              <label className="overline block mb-1">Plantilla de estadísticas *</label>
              <select data-testid="cat-template-select" value={form.template_key} onChange={(e) => setForm({ ...form, template_key: e.target.value })} className="w-full h-10 bg-card border border-border px-3 focus:border-primary focus:outline-none font-mono text-sm">
                {TEMPLATE_OPTIONS.map(t => <option key={t} value={t}>{t} — {AGE_LABELS[t]}</option>)}
              </select>
            </div>
            <div>
              <label className="overline block mb-1">Orden</label>
              <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="h-10 px-5 border border-border text-sm uppercase font-heading tracking-wider">Cancelar</button>
              <button type="submit" data-testid="submit-cat-btn" className="btn-volt">{editing ? "Guardar" : "Crear"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
